import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Avatar,
  Upload,
  message,
  Typography,
  Spin,
} from "antd";
import {
  UserOutlined,
  CameraOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  selectCurrentCustomer,
  selectCustomerToken, // Correct Selector Name from your slice
  setCustomerCredentials,
} from "@/redux/slices/customerAuthSlice";
import {
  useUpdateCustomerProfileMutation,
  useUpdateCustomerImageMutation,
} from "@/servicescustomerside/api/customerProfile/customerProfileApi";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload";
import ChangePasswordModal from "./ChangePasswordModal";

const { Title } = Typography;

const CustomerProfile = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  // 1. Get User and Token from Redux
  const user = useSelector(selectCurrentCustomer);
  const token = useSelector(selectCustomerToken); // Use correct selector

  // Local state for image upload loading
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // API Mutations
  const [updateProfile, { isLoading: isUpdatingProfile }] =
    useUpdateCustomerProfileMutation();
  const [updateImage] = useUpdateCustomerImageMutation();

  // --- 1. Populate Form on Mount ---
  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone,
        date_of_birth: user.date_of_birth ? dayjs(user.date_of_birth) : null,
      });
    }
  }, [user, form]);

  // --- 2. Handle Image Upload ---
  const handleImageUpload = async ({ file }) => {
    setIsUploadingImage(true);
    try {
      const secureUrl = await uploadToCloudinary(file);

      await updateImage({
        id: user.id,
        avatarUrl: secureUrl,
      }).unwrap();

      const updatedUser = { ...user, avatar: secureUrl };

      // 2. Dispatch with correct key "access_token"
      // Your slice expects: { user, access_token }
      const payload = { user: updatedUser, access_token: token };

      dispatch(setCustomerCredentials(payload));

      // Optional: Redundancy update (Slice handles this usually)
      localStorage.setItem("customerAuthData", JSON.stringify(payload));

      message.success("Profile picture updated!");
    } catch (error) {
      console.error(error);
      message.error("Failed to upload image.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  // --- 3. Handle Form Submission ---
  const onFinish = async (values) => {
    try {
      const payloadData = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: user.email, // Keep original email
        phone: values.phone,
        date_of_birth: values.date_of_birth
          ? values.date_of_birth.format("YYYY-MM-DD")
          : null,
      };

      await updateProfile({
        id: user.id,
        data: payloadData,
      }).unwrap();

      const updatedUser = { ...user, ...payloadData };

      // 3. Dispatch with correct key "access_token" comment
      const authPayload = { user: updatedUser, access_token: token };

      dispatch(setCustomerCredentials(authPayload));

      // Optional: Redundancy update
      localStorage.setItem("customerAuthData", JSON.stringify(authPayload));

      message.success("Profile details updated successfully!");
    } catch (error) {
      console.error(error);
      message.error("Failed to update profile.");
    }
  };

  const inputStyle = {
    height: "50px",
    borderRadius: "8px",
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    color: "#374151",
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-[20px] p-8 shadow-sm border border-gray-100">
        <Title level={3} className="font-sans mb-8">
          Personal Information
        </Title>

        {/* --- Image Upload Section --- */}
        <div className="flex flex-col items-center mb-10">
          <div className="relative group">
            <Avatar
              size={120}
              src={user?.avatar}
              icon={!user?.avatar && <UserOutlined />}
              className="bg-orange-50 text-orange-500 border-2 border-orange-100"
            />

            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                handleImageUpload({ file });
                return false;
              }}
              accept="image/*"
              disabled={isUploadingImage}
            >
              <div
                className={`absolute inset-0 bg-black/40 rounded-full flex items-center justify-center transition-opacity cursor-pointer text-white 
                ${isUploadingImage ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              >
                {isUploadingImage ? (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 24, color: "white" }}
                        spin
                      />
                    }
                  />
                ) : (
                  <CameraOutlined className="text-2xl" />
                )}
              </div>
            </Upload>
          </div>
          <span className="text-xs text-gray-400 mt-3">
            {isUploadingImage ? "Uploading..." : "Click image to change"}
          </span>
        </div>

        {/* --- Form Section --- */}
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark="optional"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            <Form.Item
              name="first_name"
              label={
                <span className="font-semibold text-gray-600">
                  First Name<span className="text-red-500">*</span>
                </span>
              }
              rules={[{ required: true, message: "Required" }]}
            >
              <Input style={inputStyle} />
            </Form.Item>

            <Form.Item
              name="last_name"
              label={
                <span className="font-semibold text-gray-600">
                  Last Name<span className="text-red-500">*</span>
                </span>
              }
              rules={[{ required: true, message: "Required" }]}
            >
              <Input style={inputStyle} />
            </Form.Item>

            <Form.Item
              name="email"
              label={
                <span className="font-semibold text-gray-600">
                  Email<span className="text-red-500">*</span>
                </span>
              }
              rules={[{ required: true, type: "email" }]}
              className="md:col-span-2"
            >
              <Input
                style={{
                  ...inputStyle,
                  cursor: "not-allowed",
                  backgroundColor: "#f3f4f6",
                }}
                readOnly
              />
            </Form.Item>

            <Form.Item
              name="phone"
              label={
                <span className="font-semibold text-gray-600">
                  Phone Number<span className="text-red-500">*</span>
                </span>
              }
              rules={[{ required: true, message: "Required" }]}
              className="md:col-span-2"
            >
              <Input style={inputStyle} />
            </Form.Item>

            <Form.Item
              name="date_of_birth"
              label={
                <span className="font-semibold text-gray-600">
                  Date of Birth<span className="text-red-500">*</span>
                </span>
              }
              rules={[{ required: true, message: "Required" }]}
              className="md:col-span-2"
            >
              <DatePicker
                style={{ ...inputStyle, width: "100%" }}
                format="DD-MM-YYYY"
              />
            </Form.Item>
          </div>

          <div className="flex justify-center mt-8">
            <Button
              type="primary"
              htmlType="submit"
              loading={isUpdatingProfile}
              className="bg-[#F79F1A] hover:!bg-[#e08905] h-12 px-12 rounded-full font-bold text-base shadow-lg shadow-orange-100 border-none"
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </div>

      <div className="bg-white rounded-[20px] p-8 shadow-sm border border-gray-100 mt-6 flex items-center justify-between">
        <div>
          <Title level={5} className="font-sans !mb-1">
            Password & Security
          </Title>
          <p className="text-gray-500 text-sm m-0">
            Update your password to keep your account secure.
          </p>
        </div>
        <Button
          onClick={() => setIsChangePasswordOpen(true)}
          className="h-11 rounded-full font-bold border-[#F79F1A] text-[#F79F1A] hover:!text-[#e08905] hover:!border-[#e08905]"
        >
          Change Password
        </Button>
      </div>

      <ChangePasswordModal
        open={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </div>
  );
};

export default CustomerProfile;
